import React, { useState, useEffect } from "react";
import * as Yup from "yup";
import { Formik, Form, Field, FieldArray } from "formik";
import { toast } from "react-toastify";
import { makeStyles } from "@material-ui/core/styles";
import { green } from "@material-ui/core/colors";
import Button from "@material-ui/core/Button";
import TextField from "@material-ui/core/TextField";
import Dialog from "@material-ui/core/Dialog";
import DialogActions from "@material-ui/core/DialogActions";
import DialogContent from "@material-ui/core/DialogContent";
import DialogTitle from "@material-ui/core/DialogTitle";
import CircularProgress from "@material-ui/core/CircularProgress";
import { i18n } from "../../translate/i18n";
import {
  FormControlLabel,
  Grid,
  MenuItem,
  FormControl,
  InputLabel,
  Select,
  Switch,
  Typography,
  Checkbox,
} from "@material-ui/core";
import { Visibility, VisibilityOff, AddCircleOutline, DeleteOutline } from "@material-ui/icons";
import { InputAdornment, IconButton } from "@material-ui/core";
import NumberFormat from "react-number-format";
import QueueSelectSingle from "../QueueSelectSingle";
import api from "../../services/api";
import toastError from "../../errors/toastError";

const useStyles = makeStyles(theme => ({
  root: {
    display: "flex",
    flexWrap: "wrap",
  },
  multFieldLine: {
    display: "flex",
    "& > *:not(:last-child)": {
      marginRight: theme.spacing(1),
    },
  },
  btnWrapper: {
    position: "relative",
  },
  buttonProgress: {
    color: green[500],
    position: "absolute",
    top: "50%",
    left: "50%",
    marginTop: -12,
    marginLeft: -12,
  },
  formControl: {
    margin: theme.spacing(1),
    minWidth: 120,
  },
  colorAdorment: {
    width: 20,
    height: 20,
  },
  scheduleSection: {
    marginTop: theme.spacing(2),
    paddingTop: theme.spacing(2),
    borderTop: `1px solid ${theme.palette.divider}`,
  },
  scheduleHeader: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    marginBottom: theme.spacing(1),
  },
  scheduleDay: {
    width: "100%",
  },
  scheduleTimeField: {
    width: "100%",
  },
}));

// Lista alinhada com o backend / TALK.AI
// Mantém os GPTs e modelos Gemini
const allowedModels = [
  "gpt-4o-mini",
  "gpt-4o",
  "gpt-4.1-mini",
  "gpt-4.1",
  "gpt-5-mini",
  "gpt-5",
  "gemini-2.5-flash",
  "gemini-2.0-flash",
  "gemini-2.0-pro",
  "gemini-3.0-flash",
  "gemini-3.0-pro",
];

const getDefaultSchedules = () => [
  { weekday: i18n.t("queueModal.serviceHours.monday"), weekdayEn: "monday", enabled: false, periods: [{ startTime: "08:00", endTime: "18:00" }] },
  { weekday: i18n.t("queueModal.serviceHours.tuesday"), weekdayEn: "tuesday", enabled: false, periods: [{ startTime: "08:00", endTime: "18:00" }] },
  { weekday: i18n.t("queueModal.serviceHours.wednesday"), weekdayEn: "wednesday", enabled: false, periods: [{ startTime: "08:00", endTime: "18:00" }] },
  { weekday: i18n.t("queueModal.serviceHours.thursday"), weekdayEn: "thursday", enabled: false, periods: [{ startTime: "08:00", endTime: "18:00" }] },
  { weekday: i18n.t("queueModal.serviceHours.friday"), weekdayEn: "friday", enabled: false, periods: [{ startTime: "08:00", endTime: "18:00" }] },
  { weekday: i18n.t("queueModal.serviceHours.saturday"), weekdayEn: "saturday", enabled: false, periods: [{ startTime: "08:00", endTime: "18:00" }] },
  { weekday: i18n.t("queueModal.serviceHours.sunday"), weekdayEn: "sunday", enabled: false, periods: [{ startTime: "08:00", endTime: "18:00" }] },
];

const normalizeSchedules = (rawSchedules) => {
  const defaults = getDefaultSchedules();
  const byWeekday = new Map((Array.isArray(rawSchedules) ? rawSchedules : []).map(item => [item?.weekdayEn, item]));

  return defaults.map(defaultDay => {
    const savedDay = byWeekday.get(defaultDay.weekdayEn);
    if (!savedDay) return defaultDay;

    const legacyPeriods = [
      { startTime: savedDay.startTimeA || "", endTime: savedDay.endTimeA || "" },
      { startTime: savedDay.startTimeB || "", endTime: savedDay.endTimeB || "" },
    ].filter(period => period.startTime || period.endTime);

    const periods = Array.isArray(savedDay.periods) && savedDay.periods.length > 0
      ? savedDay.periods
      : legacyPeriods.length > 0
        ? legacyPeriods
        : defaultDay.periods;

    return {
      ...defaultDay,
      ...savedDay,
      enabled: typeof savedDay.enabled === "boolean"
        ? savedDay.enabled
        : periods.length > 0,
      periods: periods.map(period => ({
        startTime: period?.startTime || "",
        endTime: period?.endTime || "",
      })),
    };
  });
};

const scheduleTimeSchema = Yup.string()
  .nullable()
  .matches(/^$|^([01]\d|2[0-3]):[0-5]\d$/, "Horário inválido");

const PromptSchema = Yup.object().shape({
  name: Yup.string()
    .min(5, "Muito curto!")
    .max(100, "Muito longo!")
    .required("Obrigatório"),
  prompt: Yup.string()
    .min(50, "Muito curto!")
    .required("Descreva o treinamento para Inteligência Artificial"),
  model: Yup.string()
    .oneOf(allowedModels, "Modelo inválido")
    .required("Informe o modelo"),
  maxTokens: Yup.number()
    .min(10, "Mínimo 10 tokens")
    .max(4096, "Máximo 4096 tokens")
    .required("Informe o número máximo de tokens"),
  temperature: Yup.number()
    .min(0, "Mínimo 0")
    .max(1, "Máximo 1")
    .required("Informe a temperatura"),
  apiKey: Yup.string().required("Informe a API Key"),
  queueId: Yup.number().required("Informe a fila"),
  maxMessages: Yup.number()
    .min(1, "Mínimo 1 mensagem")
    .max(50, "Máximo 50 mensagens")
    .required("Informe o número máximo de mensagens"),
  voice: Yup.string().notRequired(),
  voiceKey: Yup.string().notRequired(),
  voiceRegion: Yup.string().notRequired(),
  scheduleEnabled: Yup.boolean().notRequired(),
  schedules: Yup.array().of(
    Yup.object().shape({
      weekday: Yup.string().notRequired(),
      weekdayEn: Yup.string().notRequired(),
      enabled: Yup.boolean().notRequired(),
      periods: Yup.array().of(
        Yup.object().shape({
          startTime: scheduleTimeSchema,
          endTime: scheduleTimeSchema,
        })
      ),
    })
  ),
});

const PromptModal = ({ open, onClose, promptId }) => {
  const classes = useStyles();
  const [showApiKey, setShowApiKey] = useState(false);

  const handleToggleApiKey = () => {
    setShowApiKey(!showApiKey);
  };

  const initialState = {
    name: "",
    prompt: "",
    model: "gpt-4o-mini",
    voice: "texto",
    voiceKey: "",
    voiceRegion: "",
    maxTokens: 100,
    temperature: 1,
    apiKey: "",
    queueId: null,
    maxMessages: 10,
    scheduleEnabled: false,
    schedules: getDefaultSchedules(),
  };

  const [prompt, setPrompt] = useState(initialState);

  useEffect(() => {
    const fetchPrompt = async () => {
      if (!promptId) {
        setPrompt(initialState);
        return;
      }
      try {
        const { data } = await api.get(`/prompt/${promptId}`);

        setPrompt({
          ...initialState,
          ...data,
          queueId: data.queueId || null,
          // garante que usamos apenas modelos permitidos
          model: allowedModels.includes(data.model)
            ? data.model
            : "gpt-4o-mini",
          scheduleEnabled: Boolean(data.scheduleEnabled),
          schedules: normalizeSchedules(data.schedules),
        });
      } catch (err) {
        toastError(err);
      }
    };

    fetchPrompt();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [promptId, open]);

  const handleClose = () => {
    setPrompt(initialState);
    onClose();
  };

  const handleSavePrompt = async (values, { setSubmitting, setErrors }) => {
    try {
      const promptData = {
        ...values,
        // Converte vírgula para ponto nos campos numéricos
        temperature: typeof values.temperature === 'string'
          ? parseFloat(values.temperature.replace(',', '.'))
          : values.temperature,
        maxTokens: typeof values.maxTokens === 'string'
          ? parseInt(values.maxTokens.replace(',', '.'))
          : values.maxTokens,
        scheduleEnabled: Boolean(values.scheduleEnabled),
        schedules: Array.isArray(values.schedules)
          ? values.schedules.map(day => {
            const periods = Array.isArray(day.periods)
              ? day.periods.filter(period => period?.startTime && period?.endTime)
              : [];
            return {
              ...day,
              periods,
              // retrocompatibilidade com consumidores antigos
              startTimeA: periods[0]?.startTime || "",
              endTimeA: periods[0]?.endTime || "",
              startTimeB: periods[1]?.startTime || "",
              endTimeB: periods[1]?.endTime || "",
            };
          })
          : getDefaultSchedules(),
      };

      if (promptId) {
        await api.put(`/prompt/${promptId}`, promptData);
      } else {
        await api.post("/prompt", promptData);
      }

      toast.success(i18n.t("promptModal.success"));
      handleClose();
    } catch (err) {
      const errorMessage =
        err.response?.data?.message || "Erro ao salvar o prompt";
      toastError(errorMessage);

      try {
        const parsedError = JSON.parse(errorMessage);
        if (parsedError.errors) {
          const fieldErrors = {};
          parsedError.errors.forEach(error => {
            if (error.includes("NAME")) fieldErrors.name = error;
            if (error.includes("PROMPT")) fieldErrors.prompt = error;
            if (error.includes("MODEL")) fieldErrors.model = error;
            if (error.includes("TOKENS")) fieldErrors.maxTokens = error;
            if (error.includes("TEMPERATURE")) fieldErrors.temperature = error;
            if (error.includes("APIKEY")) fieldErrors.apiKey = error;
            if (error.includes("QUEUEID")) fieldErrors.queueId = error;
            if (error.includes("MESSAGES")) fieldErrors.maxMessages = error;
            if (error.includes("VOICE")) fieldErrors.voice = error;
          });
          setErrors(fieldErrors);
        }
      } catch (jsonError) {
        // se não for JSON, ignora e mantém erro genérico
      }

      setSubmitting(false);
    }
  };

  return (
    <div className={classes.root}>
      <Dialog
        open={open}
        onClose={handleClose}
        maxWidth="md"
        scroll="paper"
        fullWidth
      >
        <DialogTitle id="form-dialog-title">
          {promptId
            ? i18n.t("promptModal.title.edit")
            : i18n.t("promptModal.title.add")}
        </DialogTitle>
        <Formik
          initialValues={prompt}
          enableReinitialize={true}
          validationSchema={PromptSchema}
          onSubmit={handleSavePrompt}
        >
          {({ touched, errors, isSubmitting, values, setFieldValue }) => (
            <Form style={{ width: "100%" }}>
              <DialogContent dividers>
                <Field
                  as={TextField}
                  label={i18n.t("promptModal.form.name")}
                  name="name"
                  error={touched.name && Boolean(errors.name)}
                  helperText={touched.name && errors.name}
                  variant="outlined"
                  margin="dense"
                  fullWidth
                  required
                />

                <FormControl
                  fullWidth
                  margin="dense"
                  variant="outlined"
                >
                  <Field
                    as={TextField}
                    label={i18n.t("promptModal.form.apikey")}
                    name="apiKey"
                    type={showApiKey ? "text" : "password"}
                    error={touched.apiKey && Boolean(errors.apiKey)}
                    helperText={touched.apiKey && errors.apiKey}
                    variant="outlined"
                    margin="dense"
                    fullWidth
                    required
                    InputProps={{
                      endAdornment: (
                        <InputAdornment position="end">
                          <IconButton onClick={handleToggleApiKey}>
                            {showApiKey ? <VisibilityOff /> : <Visibility />}
                          </IconButton>
                        </InputAdornment>
                      ),
                    }}
                  />
                </FormControl>

                <Field
                  as={TextField}
                  label={i18n.t("promptModal.form.prompt")}
                  name="prompt"
                  error={touched.prompt && Boolean(errors.prompt)}
                  helperText={touched.prompt && errors.prompt}
                  variant="outlined"
                  margin="dense"
                  fullWidth
                  required
                  rows={10}
                  multiline
                />

                <Field
                  name="queueId"
                  component={({ field, form }) => (
                    <QueueSelectSingle
                      selectedQueueId={field.value}
                      onChange={value => form.setFieldValue("queueId", value)}
                    />
                  )}
                />

                <div className={classes.multFieldLine}>
                  <FormControl
                    fullWidth
                    margin="dense"
                    variant="outlined"
                    error={touched.model && Boolean(errors.model)}
                  >
                    <InputLabel>
                      {i18n.t("promptModal.form.model")}
                    </InputLabel>
                    <Field
                      as={Select}
                      label={i18n.t("promptModal.form.model")}
                      name="model"
                      onChange={e => {
                        setFieldValue("model", e.target.value);
                      }}
                    >
                      {allowedModels.map(model => (
                        <MenuItem key={model} value={model}>
                          {model === "gpt-4o-mini" && "GPT 4o Mini"}
                          {model === "gpt-4o" && "GPT 4o"}
                          {model === "gpt-4.1-mini" && "GPT 4.1 Mini"}
                          {model === "gpt-4.1" && "GPT 4.1"}
                          {model === "gpt-5-mini" && "GPT 5 Mini"}
                          {model === "gpt-5" && "GPT 5"}
                          {model === "gemini-2.5-flash" && "Gemini 2.5 Flash"}
                          {model === "gemini-2.0-flash" && "Gemini 2.0 Flash"}
                          {model === "gemini-2.0-pro" && "Gemini 2.0 Pro"}
                          {model === "gemini-3.0-flash" && "Gemini 3.0 Flash"}
                          {model === "gemini-3.0-pro" && "Gemini 3.0 Pro"}
                        </MenuItem>
                      ))}
                    </Field>
                    {touched.model && errors.model && (
                      <div style={{ color: "red", fontSize: "12px" }}>
                        {errors.model}
                      </div>
                    )}
                  </FormControl>

                  <FormControl
                    fullWidth
                    margin="dense"
                    variant="outlined"
                    error={touched.voice && Boolean(errors.voice)}
                  >
                    <InputLabel>
                      {i18n.t("promptModal.form.voice")}
                    </InputLabel>
                    <Field
                      as={Select}
                      label={i18n.t("promptModal.form.voice")}
                      name="voice"
                    >
                      <MenuItem value="texto">Texto</MenuItem>
                      <MenuItem value="pt-BR-FranciscaNeural">
                        Francisca
                      </MenuItem>
                      <MenuItem value="pt-BR-AntonioNeural">
                        Antônio
                      </MenuItem>
                      <MenuItem value="pt-BR-BrendaNeural">Brenda</MenuItem>
                      <MenuItem value="pt-BR-DonatoNeural">Donato</MenuItem>
                      <MenuItem value="pt-BR-ElzaNeural">Elza</MenuItem>
                      <MenuItem value="pt-BR-FabioNeural">Fábio</MenuItem>
                      <MenuItem value="pt-BR-GiovannaNeural">
                        Giovanna
                      </MenuItem>
                      <MenuItem value="pt-BR-HumbertoNeural">
                        Humberto
                      </MenuItem>
                      <MenuItem value="pt-BR-JulioNeural">Julio</MenuItem>
                      <MenuItem value="pt-BR-LeilaNeural">Leila</MenuItem>
                      <MenuItem value="pt-BR-LeticiaNeural">
                        Letícia
                      </MenuItem>
                      <MenuItem value="pt-BR-ManuelaNeural">
                        Manuela
                      </MenuItem>
                      <MenuItem value="pt-BR-NicolauNeural">
                        Nicolau
                      </MenuItem>
                      <MenuItem value="pt-BR-ValerioNeural">
                        Valério
                      </MenuItem>
                      <MenuItem value="pt-BR-YaraNeural">Yara</MenuItem>
                    </Field>
                    {touched.voice && errors.voice && (
                      <div style={{ color: "red", fontSize: "12px" }}>
                        {errors.voice}
                      </div>
                    )}
                  </FormControl>
                </div>

                <div className={classes.multFieldLine}>
                  <Field
                    as={TextField}
                    label={i18n.t("promptModal.form.voiceKey")}
                    name="voiceKey"
                    error={touched.voiceKey && Boolean(errors.voiceKey)}
                    helperText={touched.voiceKey && errors.voiceKey}
                    variant="outlined"
                    margin="dense"
                    fullWidth
                  />
                  <Field
                    as={TextField}
                    label={i18n.t("promptModal.form.voiceRegion")}
                    name="voiceRegion"
                    error={touched.voiceRegion && Boolean(errors.voiceRegion)}
                    helperText={touched.voiceRegion && errors.voiceRegion}
                    variant="outlined"
                    margin="dense"
                    fullWidth
                  />
                </div>

                <div className={classes.multFieldLine}>
                  <Field
                    as={TextField}
                    label={i18n.t("promptModal.form.temperature")}
                    name="temperature"
                    error={touched.temperature && Boolean(errors.temperature)}
                    helperText={touched.temperature && errors.temperature}
                    variant="outlined"
                    margin="dense"
                    fullWidth
                    type="number"
                    inputProps={{
                      step: "0.1",
                      min: "0",
                      max: "1",
                    }}
                  />
                  <Field
                    as={TextField}
                    label={i18n.t("promptModal.form.max_tokens")}
                    name="maxTokens"
                    error={touched.maxTokens && Boolean(errors.maxTokens)}
                    helperText={touched.maxTokens && errors.maxTokens}
                    variant="outlined"
                    margin="dense"
                    fullWidth
                    type="number"
                  />
                  <Field
                    as={TextField}
                    label={i18n.t("promptModal.form.max_messages")}
                    name="maxMessages"
                    error={touched.maxMessages && Boolean(errors.maxMessages)}
                    helperText={touched.maxMessages && errors.maxMessages}
                    variant="outlined"
                    margin="dense"
                    fullWidth
                    type="number"
                  />
                </div>

                <div className={classes.scheduleSection}>
                  <div className={classes.scheduleHeader}>
                    <Typography variant="subtitle2">
                      {i18n.t("promptModal.form.scheduleTitle")}
                    </Typography>
                    <FormControlLabel
                      control={
                        <Switch
                          checked={Boolean(values.scheduleEnabled)}
                          onChange={event =>
                            setFieldValue(
                              "scheduleEnabled",
                              event.target.checked
                            )
                          }
                          color="primary"
                        />
                      }
                      label={i18n.t("promptModal.form.scheduleEnabled")}
                    />
                  </div>

                  {values.scheduleEnabled && (
                    <FieldArray
                      name="schedules"
                      render={() => (
                        <Grid spacing={2} container>
                          {(values.schedules || []).map((item, index) => (
                            <Grid key={item.weekdayEn || index} xs={12} md={4} item>
                              <Grid container spacing={1}>
                                <Grid xs={12} item>
                                  <FormControlLabel
                                    control={
                                      <Checkbox
                                        checked={Boolean(values.schedules?.[index]?.enabled)}
                                        onChange={event =>
                                          setFieldValue(
                                            `schedules[${index}].enabled`,
                                            event.target.checked
                                          )
                                        }
                                        color="primary"
                                      />
                                    }
                                    label={item.weekday}
                                  />
                                </Grid>
                                {Boolean(values.schedules?.[index]?.enabled) && (
                                  <>
                                    {Array.isArray(item.periods) && item.periods.map((period, periodIndex) => (
                                      <React.Fragment key={`${item.weekdayEn}-${periodIndex}`}>
                                        <Grid xs={12} sm={5} item>
                                          <Field name={`schedules[${index}].periods[${periodIndex}].startTime`}>
                                            {({ field }) => (
                                              <NumberFormat
                                                {...field}
                                                variant="outlined"
                                                margin="dense"
                                                customInput={TextField}
                                                format="##:##"
                                                className={classes.scheduleTimeField}
                                                label="Hora inicial"
                                              />
                                            )}
                                          </Field>
                                        </Grid>
                                        <Grid xs={12} sm={5} item>
                                          <Field name={`schedules[${index}].periods[${periodIndex}].endTime`}>
                                            {({ field }) => (
                                              <NumberFormat
                                                {...field}
                                                variant="outlined"
                                                margin="dense"
                                                customInput={TextField}
                                                format="##:##"
                                                className={classes.scheduleTimeField}
                                                label="Hora final"
                                              />
                                            )}
                                          </Field>
                                        </Grid>
                                        <Grid xs={12} sm={2} item style={{ display: "flex", alignItems: "center" }}>
                                          <IconButton
                                            size="small"
                                            onClick={() => {
                                              const periods = values.schedules[index].periods || [];
                                              if (periods.length > 1) {
                                                const nextPeriods = periods.filter((_, idx) => idx !== periodIndex);
                                                setFieldValue(`schedules[${index}].periods`, nextPeriods);
                                              }
                                            }}
                                          >
                                            <DeleteOutline fontSize="small" />
                                          </IconButton>
                                        </Grid>
                                      </React.Fragment>
                                    ))}
                                    <Grid xs={12} item>
                                      <Button
                                        size="small"
                                        color="primary"
                                        startIcon={<AddCircleOutline />}
                                        onClick={() => {
                                          const periods = values.schedules[index].periods || [];
                                          setFieldValue(`schedules[${index}].periods`, [
                                            ...periods,
                                            { startTime: "", endTime: "" },
                                          ]);
                                        }}
                                      >
                                        Adicionar horário
                                      </Button>
                                    </Grid>
                                  </>
                                )}
                              </Grid>
                            </Grid>
                          ))}
                        </Grid>
                      )}
                    />
                  )}
                </div>
              </DialogContent>

              <DialogActions>
                <Button
                  onClick={handleClose}
                  color="secondary"
                  disabled={isSubmitting}
                  variant="outlined"
                >
                  {i18n.t("promptModal.buttons.cancel")}
                </Button>
                <Button
                  type="submit"
                  color="primary"
                  disabled={isSubmitting}
                  variant="contained"
                  className={classes.btnWrapper}
                >
                  {promptId
                    ? i18n.t("promptModal.buttons.okEdit")
                    : i18n.t("promptModal.buttons.okAdd")}
                  {isSubmitting && (
                    <CircularProgress
                      size={24}
                      className={classes.buttonProgress}
                    />
                  )}
                </Button>
              </DialogActions>
            </Form>
          )}
        </Formik>
      </Dialog>
    </div>
  );
};

export default PromptModal;
